import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { RepairService } from 'src/app/shared/services/repair.service';

export type LightSectionModAction = 'ReplaceGeometry' | 'HideOnly' | 'AddGeometry'
  | 'replace_geometry' | 'hide_only' | 'add_geometry'
  | 0 | 1 | 2;

export interface LightSectionModGeometry {
  V: number[];
  F: number[];
  BoundsMin?: number[];
  BoundsMax?: number[];
}

export interface LightSectionMod {
  ModId: string;
  RepairId: string;
  ElementId: string;
  RegionId: string;
  SectionId: string;
  TypeId: string;
  Action: LightSectionModAction;
  PostGeometry?: LightSectionModGeometry;
  Source: string;
  OriginatedElements?: string[];
}

export interface LightSectionModFile {
  AssetId: string;
  RegionId: string;
  SectionId: string;
  Date: string | Date;
  CreatedAtUtc?: string | Date;
  RepairFileRef?: string;
  SchemaVersion?: number;
  Mods: LightSectionMod[];
}

interface DatedMod { date: Date; mod: LightSectionMod; }

interface RegionEntry {
  mods: LightSectionMod[];
  byElement: Map<string, DatedMod[]>;
  bySection: Map<string, DatedMod[]>;
  loaded: boolean;
  inflight: Promise<void> | null;
}

@Injectable({ providedIn: 'root' })
export class LightSectionModService {
  public readonly modsChanged$ = new Subject<void>();

  // Per-region cache. Keyed by `${assetId}|${regionId}` so we can hold state for
  // multiple regions concurrently (e.g. user navigates R3 → R2 → R3) without
  // re-fetching. Mirrors the repair flow's per-region fetch pattern.
  private currentAssetId: string | null = null;
  private regions: Map<string, RegionEntry> = new Map();

  constructor(private readonly repairService: RepairService) {}

  public async preloadForRegion(assetId: string, regionId: string): Promise<void> {
    if (!regionId) return;
    if (this.currentAssetId !== assetId) {
      // Switching asset — drop everything we cached for the old one.
      this.currentAssetId = assetId;
      this.regions.clear();
    }

    const key = this.regionKey(regionId);
    const entry = this.regions.get(key);
    if (entry?.loaded) return;
    if (entry?.inflight) return entry.inflight;

    const fresh: RegionEntry = {
      mods: [],
      byElement: new Map(),
      bySection: new Map(),
      loaded: false,
      inflight: null,
    };
    fresh.inflight = (async () => {
      const files = await this.repairService.getRegionLightSectionMods(assetId, regionId);
      this.indexInto(fresh, files as LightSectionModFile[]);
      fresh.loaded = true;
      fresh.inflight = null;
      this.modsChanged$.next();
    })();
    this.regions.set(key, fresh);

    try { await fresh.inflight; } finally { /* cleared above */ }
  }

  /**
   * Force-refresh after a repair mutation. Drops just the given region's cache
   * and reloads. The renderer auto-re-applies via `modsChanged$`.
   */
  public async refresh(assetId: string, regionId: string): Promise<void> {
    if (!regionId) return;
    this.regions.delete(this.regionKey(regionId));
    await this.preloadForRegion(assetId, regionId);
  }

  public invalidate(): void {
    this.currentAssetId = null;
    this.regions.clear();
  }

  public isLoaded(): boolean {
    // Loaded = at least one region has data. The renderer uses this as a gate
    // for applyForDate; an empty store legitimately yields no overlays.
    for (const entry of this.regions.values()) {
      if (entry.loaded) return true;
    }
    return false;
  }

  public isRegionLoaded(regionId: string): boolean {
    return this.regions.get(this.regionKey(regionId))?.loaded === true;
  }

  public allMods(): readonly LightSectionMod[] {
    const all: LightSectionMod[] = [];
    for (const entry of this.regions.values()) {
      if (entry.loaded) all.push(...entry.mods);
    }
    return all;
  }

  /**
   * Returns the active mod per (regionId, elementId) as of referenceDate.
   * Active = most recent mod whose date is on or before referenceDate.
   * The renderer keys its handles by (regionId, elementId), so the same
   * elementId in two different regions stays separately addressable.
   */
  public getActiveModsAt(referenceDate: Date): Map<string, LightSectionMod> {
    const result = new Map<string, LightSectionMod>();
    const refTime = referenceDate.getTime();
    for (const entry of this.regions.values()) {
      if (!entry.loaded) continue;
      for (const [elementId, history] of entry.byElement) {
        let active: LightSectionMod | null = null;
        for (const datedMod of history) {
          if (datedMod.date.getTime() <= refTime) active = datedMod.mod;
          else break;
        }
        if (active) {
          // Composite key keeps cross-region elements from colliding.
          result.set(this.activeKey(active.RegionId, elementId), active);
        }
      }
    }
    return result;
  }

  public getModsForSectionAt(regionId: string, sectionId: string, referenceDate: Date): LightSectionMod[] {
    const entry = this.regions.get(this.regionKey(regionId));
    if (!entry?.loaded) return [];
    const history = entry.bySection.get(sectionId);
    if (!history) return [];
    const refTime = referenceDate.getTime();
    const byElement = new Map<string, LightSectionMod>();
    for (const datedMod of history) {
      if (datedMod.date.getTime() > refTime) break;
      byElement.set(datedMod.mod.ElementId, datedMod.mod);
    }
    return Array.from(byElement.values());
  }

  private indexInto(entry: RegionEntry, files: LightSectionModFile[]): void {
    entry.mods = [];
    entry.byElement.clear();
    entry.bySection.clear();

    for (const file of files ?? []) {
      const date = new Date(file.Date);
      const sectionId = file.SectionId;
      for (const mod of file.Mods ?? []) {
        entry.mods.push(mod);
        const datedMod: DatedMod = { date, mod };

        const elArr = entry.byElement.get(mod.ElementId) ?? [];
        elArr.push(datedMod);
        entry.byElement.set(mod.ElementId, elArr);

        const secArr = entry.bySection.get(sectionId) ?? [];
        secArr.push(datedMod);
        entry.bySection.set(sectionId, secArr);
      }
    }

    const byTime = (a: DatedMod, b: DatedMod) => a.date.getTime() - b.date.getTime();
    for (const arr of entry.byElement.values()) arr.sort(byTime);
    for (const arr of entry.bySection.values()) arr.sort(byTime);
  }

  private regionKey(regionId: string): string {
    return regionId;
  }

  private activeKey(regionId: string, elementId: string): string {
    return `${regionId}|${elementId}`;
  }
}

export function isHideAction(action: LightSectionModAction): boolean {
  return action === 'HideOnly' || action === 'hide_only' || action === 1;
}

export function isAddAction(action: LightSectionModAction): boolean {
  return action === 'AddGeometry' || action === 'add_geometry' || action === 2;
}

export function isReplaceAction(action: LightSectionModAction): boolean {
  return action === 'ReplaceGeometry' || action === 'replace_geometry' || action === 0;
}
